// 仅当图片属于合法域名时才走 Cloudflare 拼接，否则返回原始图片
// 合法域名请同步 next.config.ts 中 images.domains 配置

declare const process: { env: Record<string, string | undefined> };

const sanitizeHost = (host?: string) =>
	(host || "")
		.replace(/^https?:\/\//i, "")
		.replace(/\/$/, "");

const R2_HOST = sanitizeHost(process.env.NEXT_PUBLIC_R2_ADMIN);

const ALLOWED_DOMAINS = [R2_HOST, "prod-eurasian-res.popmart.com"];

const isAllowedDomain = (src: string): boolean => {
	try {
		const url = new URL(src.startsWith("//") ? `https:${src}` : src);
		return ALLOWED_DOMAINS.some(
			(domain) => domain && url.hostname.endsWith(domain),
		);
	} catch {
		// src 不是完整 URL，直接返回 false
		return false;
	}
};

const normalizeSrc = (src: string) => {
	return src.startsWith("/") ? src.slice(1) : src;
};

export default function cloudflareLoader({
	src,
	width,
	quality,
}: {
	src: string;
	width: number;
	quality?: number;
}) {
	// Basic validation to avoid emitting invalid href/src
	if (!src || typeof src !== "string") {
		return src as unknown as string;
	}

	// Normalize protocol-relative URLs to https
	if (src.startsWith("//")) {
		src = `https:${src}`;
	}
	// Handle static assets (Next.js internal images)
	if (src.startsWith("/_next/static/")) {
		return src;
	}

	// Handle local images
	if (src.startsWith("/")) {
		return src;
	}

	// 仅合法域名才走 cloudflare 拼接
	if (!isAllowedDomain(src)) {
		return src;
	}

	// Cloudflare host missing -> skip rewriting to avoid invalid preload href
	if (!R2_HOST) {
		return src;
	}

	const params = [`width=${width}`];
	if (quality) {
		params.push(`quality=${quality}`);
	}
	params.push("format=webp");
	// Encode only commas so they don't break srcset candidate parsing
	const paramsString = params.join(",").replace(/,/g, "%2C");
	// Encode the source URL so spaces and special chars don't create invalid descriptors
	let encodedSrc = encodeURI(normalizeSrc(src)).replace(/,/g, "%2C");
	return `https://${R2_HOST}/cdn-cgi/image/${paramsString}/${encodedSrc}`;
}
